// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import './AssessmentInitiation.cardRoot.css';
import { CSSProperties } from 'react';

const colors = {
  cardBg: '#FFFFFF',
  cardBorder: '#0000000D',
  cardShadow: '0 1px 3px rgba(0,0,0,0.08)',
  label: '#78716C',
  inputBg: '#F5F5F4',
  textPrimary: '#1C1917',
  textSecondary: '#57534E',
  purpleBadge: '#6D28D9',
  strictCardBorder: '#0000000D',
  coachCardBg: '#4F46E5',
  coachCardText: '#FFFFFF',
  greenButton: '#00D2AA',
  greenButtonHover: '#00B894'
} as const;

export const cardRoot: CSSProperties = {
  backgroundColor: colors.cardBg,
  borderRadius: 16,
  boxShadow: colors.cardShadow,
  border: `1px solid ${colors.strictCardBorder}`,
  padding: 32,
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  margin: '0 auto',
  maxWidth: 1200
};

export const badge: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  backgroundColor: 'rgba(69, 83, 145, 0.05)',
  color: '#455391',
  fontSize: 18,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
  position: 'absolute',
  left: 30,
  top: 30
};

export const title: CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  color: colors.textPrimary,
  margin: '0 0 8px 50px',
  textTransform: 'uppercase'
};

export const subtitle: CSSProperties = {
  fontSize: 20,
  color: colors.textSecondary,
  margin: '10px 0 15px 0',
  textAlign: 'center'
};
export const subtitleMini: CSSProperties = {
  fontSize: 14,
  color: colors.textSecondary,
  margin: '0 0 20px 0',
  textAlign: 'center',
  fontWeight: 300
};
export const mainContentWrapper: CSSProperties = {
  margin: '0 auto',
  maxWidth: '100%'
};

export const formGroup: CSSProperties = {
  marginBottom: 20
};

export const formRow: CSSProperties = {
  display: 'flex',
  gap: 16,
  marginBottom: 20
};

export const formGroupInRow: CSSProperties = {
  flex: 1,
  minWidth: 0
};

export const label: CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  color: colors.label,
  textTransform: 'capitalize',
  marginBottom: 8
};

export const select: CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  fontSize: 14,
  color: colors.textPrimary,
  backgroundColor: '#ffffff',
  border: `1px solid ${colors.strictCardBorder}`,
  borderRadius: 8,
  appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2378716C' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 16px center',
  paddingRight: 40,
  boxSizing: 'border-box',
  cursor: 'pointer',
  fontFamily: 'G42 Sans'
};

export const selectError: CSSProperties = {
  border: '1px solid #d32f2f',
  outline: 'none'
};

export const selectFieldWrapper: CSSProperties = {
  position: 'relative',
  width: '100%'
};

export const fieldErrorPopover: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 6,
  padding: '8px 12px',
  fontSize: 12,
  color: '#b91c1c',
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  zIndex: 20,
  maxWidth: '100%',
  boxSizing: 'border-box'
};

export const multiSelectWrapper: CSSProperties = {
  position: 'relative',
  width: '100%'
};

export const multiSelectTrigger: CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  fontSize: 14,
  color: colors.textPrimary,
  backgroundColor: colors.inputBg,
  border: `1px solid ${colors.strictCardBorder}`,
  borderRadius: 8,
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
};

export const multiSelectTriggerLabel: CSSProperties = {
  flex: 1,
  marginRight: 8,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  textAlign: 'left'
};

export const multiSelectDropdown: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: 4,
  backgroundColor: '#FFFFFF',
  border: `1px solid ${colors.strictCardBorder}`,
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  maxHeight: 220,
  overflowY: 'auto',
  padding: '8px 12px',
  zIndex: 10
};

export const multiSelectOptionRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  color: colors.textSecondary,
  marginBottom: 4,
  padding: '8px 12px',
  boxSizing: 'border-box'
};

export const multiSelectOptionSelected: CSSProperties = {
  backgroundColor: 'rgba(0, 0, 0, 0.06)',
  fontWeight: 600
};

export const multiSelectHelperText: CSSProperties = {
  marginTop: 6,
  fontSize: 11,
  color: colors.textSecondary
};

export const multiSelectErrorText: CSSProperties = {
  marginTop: 4,
  fontSize: 11,
  color: '#B91C1C'
};

export const input: CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  fontSize: 14,
  color: colors.textPrimary,
  backgroundColor: '#ffffff',
  border: `1px solid ${colors.strictCardBorder}`,
  borderRadius: 8,
  boxSizing: 'border-box',
  fontFamily: 'G42 Sans'
};

export const inputRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8
};

export const tag: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: colors.textSecondary,
  backgroundColor: colors.inputBg,
  padding: '4px 8px',
  borderRadius: 6,
  flexShrink: 0
};

export const modeRow: CSSProperties = {
  display: 'flex',
  gap: 16
};

const modeSelectedBg = '#455391';

const modeCardBorderUnselected = '#0000000d';

export const modeCardBase: CSSProperties = {
  flex: 1,
  padding: 16,
  borderRadius: 24,
  border: `1px solid ${modeCardBorderUnselected}`,
  backgroundColor: colors.cardBg,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s, border-radius 0.2s'
};

export const modeCardSelected: CSSProperties = {
  ...modeCardBase,
  backgroundColor: modeSelectedBg,
  borderColor: modeSelectedBg,
  borderRadius: 24,
  boxShadow: '0px 8px 10px -6px #45539133, 0px 20px 25px -5px #45539133'
};

export const modeCardStrict: CSSProperties = {
  ...modeCardBase
};

export const modeCardCoach: CSSProperties = {
  ...modeCardBase
};

export const modeCardTitle: CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: colors.textPrimary,
  margin: '0 0 6px 0'
};

export const modeCardTitleActive: CSSProperties = {
  ...modeCardTitle,
  color: '#FFFFFF'
};

export const modeCardDesc: CSSProperties = {
  fontSize: 13,
  color: colors.textSecondary,
  margin: 0,
  lineHeight: 1.4
};

export const modeCardDescActive: CSSProperties = {
  ...modeCardDesc,
  color: 'rgba(255,255,255,0.9)'
};

export const startButtonRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  marginTop: 8
};

export const disclaimer: CSSProperties = {
  fontSize: 12,
  color: '#2a312b',
  margin: '16px 0 0 0',
  lineHeight: 1.5,
  fontWeight: 300,
  textAlign: 'center',
  marginBottom: 15
};

export const startButton: CSSProperties = {
  width: 250,
  maxWidth: 250,
  margin: 0,
  padding: '10px 8px 12px 15px',
  backgroundColor: '#00d2aa',
  color: '#2a312b',
  border: 'none',
  borderRadius: 999,
  boxShadow: '0 2px 8px rgba(6, 185, 143, 0.25)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  fontFamily: 'G42 Sans',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: 20,
  lineHeight: '17px',
  letterSpacing: '-0.04em',
  transition: 'all 0.2s ease-in-out'
};
